import React, { useState, useRef, useEffect } from "react";
import { IconButton, Tooltip, TextField } from "@mui/material";
import Grid2 from '@mui/material/Grid2';
import LaunchIcon from "@mui/icons-material/Launch";
import { useTheme } from "@mui/material";

interface StandardNodeHeaderProps {
  title: string;
  subtitle?: string;
  secondaryText?: string;

  backgroundColor?: string;
  textColor?: string;
  secondaryTextColor?: string;

  linkUrl?: string;
  linkTooltip?: string;
  linkIcon?: React.ReactNode;

  leftIcon?: React.ReactNode;

  rightContent?: React.ReactNode;

  padding?: string | number;
  alignItems?: "center" | "flex-start" | "flex-end";

  onTitleClick?: () => void;

  /** required for saving */
  apiNodeUpdateFieldFn?: (
    nodeId: number | string,
    field: string,
    value: any
  ) => Promise<void>;

  /** needed so we can update */
  nodeId?: number | string;
}

const StandardNodeHeader: React.FC<StandardNodeHeaderProps> = ({
  title,
  subtitle,
  secondaryText,
  backgroundColor,
  textColor,
  secondaryTextColor,
  linkUrl,
  linkTooltip = "Open link",
  linkIcon,
  leftIcon,
  rightContent,
  padding = "10px",
  alignItems = "flex-start",
  onTitleClick,
  apiNodeUpdateFieldFn,
  nodeId,
}) => {
  const theme = useTheme();

  const textMain = textColor || theme.custom.nodes.text.primary.dark;
  const textSecondary =
    secondaryTextColor || theme.custom.nodes.text.secondary.dark;

  // --- Inline Editing State ---
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(title);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Auto-focus when editing toggles on
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const saveTitle = async () => {
    if (!apiNodeUpdateFieldFn || !nodeId) {
      setIsEditing(false);
      return;
    }

    if (editValue.trim() !== title.trim()) {
      await apiNodeUpdateFieldFn(nodeId, "name", editValue.trim());
    }

    setIsEditing(false);
  };

  const cancelEdit = () => {
    setEditValue(title);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") saveTitle();
    if (e.key === "Escape") cancelEdit();
  };
  
  return (
    <div
      style={{
        backgroundColor: backgroundColor || theme.custom.base.primary.dark,
        color: textMain,
        padding,
        display: "flex",
        justifyContent: "space-between",
        alignItems,
      }}
    >
      {/* Left Section */}
      <Grid2 container direction="column">
        <div
          style={{
            fontWeight: "bold",
            fontSize: "1.05em",
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}
          onDoubleClick={() => setIsEditing(true)}
        >
          {leftIcon && <span>{leftIcon}</span>}

          {/* Editable or Static Title */}
          {isEditing ? (
            <TextField
              inputRef={inputRef}
              value={editValue}
              variant="standard"
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={saveTitle}
              onKeyDown={handleKeyDown}
              InputProps={{
                style: { fontSize: "1.05em", fontWeight: "bold" },
              }}
            />
          ) : (
            <span style={{ cursor: "text" }}>{title}</span>
          )}
        </div>

        {(subtitle || secondaryText) && (
          <div
            style={{
              fontSize: "0.85em",
              opacity: 0.9,
              color: textSecondary,
            }}
          >
            {subtitle && <span>{subtitle}</span>}
            {subtitle && secondaryText && " • "}
            {secondaryText && <span>{secondaryText}</span>}
          </div>
        )}
      </Grid2>

      {/* Right Section */}
      <Grid2 style={{ textAlign: "right", flexShrink: 0 }}>
        {rightContent ? (
          rightContent
        ) : linkUrl ? (
          <Tooltip title={linkTooltip}>
            <IconButton
              onClick={() =>
                window.open(linkUrl, "_blank", "noopener,noreferrer")
              }
              sx={{
                color: textMain,
                "&:hover": { color: "grey" },
                padding: 0,
              }}
            >
              {linkIcon || <LaunchIcon />}
            </IconButton>
          </Tooltip>
        ) : null}
      </Grid2>
    </div>
  );
};

export default StandardNodeHeader;
